import { useEffect, useState, useCallback } from "react";
import { TrendingUp, Loader2, AlertTriangle, RefreshCw, ShoppingBag, CalendarRange } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";

// Pronóstico de demanda por SKU calculado desde las ventas reales del PoS.
export default function RetailDemand() {
  const [data, setData] = useState(null);
  const [seasonal, setSeasonal] = useState(null);
  const [window, setWindow] = useState(30);
  const [loading, setLoading] = useState(true);
  const [buying, setBuying] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/retail/demand/forecast`, { params: { window } }).then((r) => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
    api.get(`/retail/demand/seasonal`).then((r) => setSeasonal(r.data)).catch(() => {});
  }, [window]);
  useEffect(() => { load(); }, [load]);

  const autoOC = async () => {
    setBuying(true);
    try {
      const { data: res } = await api.post(`/retail/purchase-orders/auto`, null, { params: { window } });
      toast.success(`OC ${res.purchase_order.folio} generada con ${res.skus} SKU(s) de reorden`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Sin SKUs por reordenar"); } finally { setBuying(false); }
  };

  const r = data?.resumen || {};
  const maxS = Math.max(1, ...((seasonal?.rows || []).map((x) => x.ventas)));
  return (
    <div data-testid="retail-demand">
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <div className="flex items-center gap-3 flex-wrap">
          {seasonal && (
            <button onClick={autoOC} disabled={buying} className="btn btn-primary text-sm" data-testid="demand-auto-oc">
              {buying ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShoppingBag className="w-4 h-4" />} Generar OC de reorden
            </button>
          )}
        </div>
        <div className="flex items-center gap-2">
          <select value={window} onChange={(e) => setWindow(Number(e.target.value))} className="field-sm" data-testid="demand-window">
            {[14, 30, 60, 90].map((w) => <option key={w} value={w}>Últimos {w} días</option>)}
          </select>
          <button onClick={load} className="btn btn-ghost text-sm"><RefreshCw className="w-4 h-4" /></button>
        </div>
      </div>

      {seasonal && seasonal.rows?.some((x) => x.ventas > 0) && (
        <div className="card-surface p-4 mb-5" data-testid="demand-seasonal">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2 overline text-navy/40"><CalendarRange className="w-3.5 h-3.5" /> Estacionalidad (12 meses)</div>
            <div className="text-xs text-navy/50">Pico: <b>{seasonal.mes_pico}</b> · Valle: <b>{seasonal.mes_valle}</b></div>
          </div>
          <div className="flex items-end gap-1.5 h-24">
            {seasonal.rows.map((m) => (
              <div key={m.mes} className="flex-1 flex flex-col items-center gap-1" title={`${m.mes}: índice ${m.indice}`}>
                <div className={`w-full rounded-t transition-all ${m.actual ? "bg-gold" : "bg-gradient-violet"}`} style={{ height: `${Math.max((m.ventas / maxS) * 100, 2)}%` }} />
                <span className="text-[9px] text-navy/40">{m.mes}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-3 gap-3 mb-5 max-w-lg">
        {[["SKUs analizados", r.skus || 0], ["Unidades vendidas", r.unidades_vendidas || 0], ["Por reordenar", r.por_reordenar || 0]].map(([l, v], i) => (
          <div key={l} className="card-surface p-3"><div className="text-[11px] text-navy/40">{l}</div><div className={`text-lg font-medium ${i === 2 && v > 0 ? "text-amber-600" : ""}`}>{v}</div></div>
        ))}
      </div>

      {loading ? (
        <div className="py-12 flex justify-center text-navy/40"><Loader2 className="w-6 h-6 animate-spin" /></div>
      ) : !data?.rows?.length ? (
        <div className="card-surface p-8 text-center text-navy/40 text-sm">Aún no hay ventas para pronosticar. Registra ventas en el <b>Punto de Venta</b>.</div>
      ) : (
        <div className="card-surface overflow-hidden">
          <div className="grid grid-cols-[1.6fr_repeat(5,1fr)_90px] gap-2 px-4 py-2.5 border-b border-navy/10 text-[11px] text-navy/40 uppercase tracking-wider">
            <span>SKU / Producto</span><span className="text-right">Vendidas</span><span className="text-right">Prom./día</span><span className="text-right">Proy. 30d</span><span className="text-right">Stock</span><span className="text-right">Cobertura</span><span className="text-right">Reorden</span>
          </div>
          {data.rows.map((row) => (
            <div key={row.sku} data-testid={`demand-row-${row.sku}`} className="grid grid-cols-[1.6fr_repeat(5,1fr)_90px] gap-2 px-4 py-2.5 border-b border-navy/5 last:border-0 items-center text-sm">
              <div className="min-w-0"><div className="truncate">{row.descripcion}</div><div className="text-[11px] text-navy/40">{row.sku}</div></div>
              <span className="text-right">{row.vendidas}</span>
              <span className="text-right text-navy/60">{row.promedio_diario}</span>
              <span className="text-right text-navy/60">{row.proyeccion_30d}</span>
              <span className="text-right">{row.stock}</span>
              <span className={`text-right ${row.cobertura_dias != null && row.cobertura_dias < 15 ? "text-amber-600 font-medium" : "text-navy/60"}`}>{row.cobertura_dias != null ? `${row.cobertura_dias}d` : "—"}</span>
              <span className="text-right">
                {row.reordenar
                  ? <span className="inline-flex items-center gap-1 text-amber-600 font-medium" title="Sugerencia de reorden"><AlertTriangle className="w-3.5 h-3.5" />{row.sugerido_reorden}</span>
                  : <span className="text-navy/25">—</span>}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
